import React, { useState, useEffect } from 'react';
import './YearComparison.css';
import { loadMonthData } from '../utils/storage';
import { COUNTRIES } from './CountrySelector';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const YearComparison = () => {
  const [years, setYears] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [comparisonData, setComparisonData] = useState({});

  useEffect(() => {
    // Obtener todos los años disponibles desde localStorage
    const availableYears = [];
    for (let year = 2020; year <= 2030; year++) {
      // Verificar si hay datos para este año
      let hasData = false;
      for (let month = 0; month < 12; month++) {
        const data = loadMonthData(month, year);
        if (data) {
          hasData = true;
          break;
        }
      }
      if (hasData) {
        availableYears.push(year);
      }
    }
    setYears(availableYears);
    // Seleccionar los últimos 3 años por defecto
    if (availableYears.length > 0) {
      setSelectedYears(availableYears.slice(-3));
    }
  }, []);

  useEffect(() => {
    calculateComparison();
    const interval = setInterval(calculateComparison, 2000);
    return () => clearInterval(interval);
  }, [selectedYears]);

  const calculateComparison = () => {
    const data = {};
    
    selectedYears.forEach(year => {
      let totalViajes = 0;
      let totalTransportes = 0;
      let totalAlojamientos = 0;
      let gastoReal = 0;
      const paisesSet = new Set();
      let mesesConViajes = 0;

      for (let i = 0; i < 12; i++) {
        const monthData = loadMonthData(i, year);
        
        if (monthData?.plan?.viajes) {
          const viajes = monthData.plan.viajes;
          if (viajes.length > 0) mesesConViajes++;
          
          viajes.forEach(viaje => {
            totalViajes++;
            
            if (viaje.pais) {
              paisesSet.add(viaje.pais);
            }
            
            const transportes = viaje.transportes || (viaje.vuelos ? viaje.vuelos.map(v => ({ tipo: 'vuelo', ...v })) : []);
            if (transportes && transportes.length > 0) {
              totalTransportes += transportes.length;
            }
            
            if (viaje.airbnbs) {
              totalAlojamientos += viaje.airbnbs.length;
            }
          });
        }
        
        if (monthData?.reflection?.presupuesto?.items) {
          monthData.reflection.presupuesto.items.forEach(item => {
            if (item.real) {
              const real = parseFloat(item.real.toString().replace(/[^0-9.,-]/g, '').replace(',', '.')) || 0;
              gastoReal += real;
            }
          });
        }
      }

      data[year] = {
        totalViajes,
        totalTransportes,
        totalAlojamientos,
        gastoReal,
        paisesUnicos: paisesSet.size,
        mesesConViajes
      };
    });

    setComparisonData(data);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const toggleYear = (year) => {
    if (selectedYears.includes(year)) {
      setSelectedYears(selectedYears.filter(y => y !== year));
    } else {
      setSelectedYears([...selectedYears, year].sort());
    }
  };

  if (years.length === 0) {
    return (
      <div className="year-comparison-container">
        <div className="empty-state">
          <p>No hay datos de años para comparar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="year-comparison-container">
      <header className="comparison-header">
        <h1 className="comparison-title">Comparación de Años</h1>
        <p className="comparison-subtitle">Selecciona los años que deseas comparar</p>
      </header>

      <div className="year-selector-section">
        <div className="year-checkboxes">
          {years.map(year => (
            <label key={year} className="year-checkbox-label">
              <input
                type="checkbox"
                checked={selectedYears.includes(year)}
                onChange={() => toggleYear(year)}
              />
              <span className="year-checkbox-text">{year}</span>
            </label>
          ))}
        </div>
      </div>

      {selectedYears.length > 0 && (
        <div className="comparison-table-container">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Métrica</th>
                {selectedYears.map(year => (
                  <th key={year}>{year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="metric-label">
                  <span className="metric-icon">✈️</span>
                  Viajes Planificados
                </td>
                {selectedYears.map(year => (
                  <td key={year} className="metric-value">
                    {comparisonData[year]?.totalViajes || 0}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="metric-label">
                  <span className="metric-icon">🛫</span>
                  Transportes Reservados
                </td>
                {selectedYears.map(year => (
                  <td key={year} className="metric-value">
                    {comparisonData[year]?.totalTransportes || 0}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="metric-label">
                  <span className="metric-icon">🏠</span>
                  Alojamientos
                </td>
                {selectedYears.map(year => (
                  <td key={year} className="metric-value">
                    {comparisonData[year]?.totalAlojamientos || 0}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="metric-label">
                  <span className="metric-icon">💸</span>
                  Gasto Real
                </td>
                {selectedYears.map(year => (
                  <td key={year} className="metric-value">
                    {formatCurrency(comparisonData[year]?.gastoReal || 0)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="metric-label">
                  <span className="metric-icon">🗺️</span>
                  Países Únicos
                </td>
                {selectedYears.map(year => (
                  <td key={year} className="metric-value">
                    {comparisonData[year]?.paisesUnicos || 0}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="metric-label">
                  <span className="metric-icon">📅</span>
                  Meses con Viajes
                </td>
                {selectedYears.map(year => (
                  <td key={year} className="metric-value">
                    {comparisonData[year]?.mesesConViajes || 0}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default YearComparison;

